import { BODY_FONT, DISPLAY_FONT, GLASS } from '../ui/theme.ts';
/**
 * Canvas2D chart primitives for the dashboards. All pure drawing; layout helpers that are
 * worth testing (scales, treemap) are exported.
 */

/**
 * Panel colours by role. Data colours (teal to grey) are the same in both modes: some
 * charts capture them when they load, and they read as fills on charcoal and on white.
 * Everything else (surfaces, text, tracks, lines) follows the mode. The lobby is dark; the
 * study rooms are the warm chamber (2026-10-05).
 */
const DARK_PALETTE = {
  bg: '#0e131b',
  panel: '#161e29',
  grid: 'rgba(255,255,255,0.07)',
  text: '#f2f6fb',
  textSoft: '#d6dee8',
  muted: '#a9b6c6',
  accentText: '#9beaff',
  teal: '#3fd6ff',
  tealDim: '#1593b8',
  navy: '#3d7bff',
  amber: '#f2b44b',
  crimson: '#ff5d73',
  forest: '#3fd69a',
  violet: '#a58bff',
  grey: '#7f8a98',
  hover: 'rgba(155,234,255,0.12)',
  selected: 'rgba(63,214,255,0.28)',
  /** Chips, keys and tiles on a panel, and their hovered and strong variants. */
  raised: '#1e2836',
  raisedHover: '#273648',
  raisedStrong: '#22344d',
  /** Empty bar tracks, faint cells, guide lines, idle marks. */
  track: 'rgba(255,255,255,0.10)',
  faint: 'rgba(255,255,255,0.04)',
  line: 'rgba(255,255,255,0.4)',
  idle: '#2c3a4d',
  /** Text on a teal or amber fill. */
  onAccent: '#04141c',
  /** Whole-panel surfaces: solid, soft, tooltip, the see-through record sheet. */
  surface: 'rgba(18,24,34,0.96)',
  surfaceSoft: 'rgba(18,24,34,0.6)',
  overlay: 'rgba(11,15,21,0.94)',
  sheet: 'rgba(10,20,36,0.42)',
  sheetShadow: 'rgba(0,0,0,0.7)',
  field: '#18222f',
  fieldIdle: '#17273f',
};

export type UiMode = 'dark' | 'chamber';
export type Palette = typeof DARK_PALETTE;

/**
 * The Clinical Intelligence Chamber (study rooms, 2026-10-05): a warm room with deep teal
 * glass for information, as in the user's reference image. Ivory and pale cyan type, soft
 * cyan highlights; data colours unchanged.
 */
const CHAMBER_PALETTE: Palette = {
  ...DARK_PALETTE,
  bg: '#0d2230',
  panel: '#12293a',
  grid: 'rgba(190,235,250,0.09)',
  text: '#eef6fa',
  textSoft: '#d3e3ec',
  muted: '#9db7c6',
  accentText: '#8fe3f0',
  hover: 'rgba(143,227,240,0.12)',
  selected: 'rgba(143,227,240,0.24)',
  raised: '#183447',
  raisedHover: '#21425a',
  raisedStrong: '#1d3c52',
  track: 'rgba(190,235,250,0.10)',
  faint: 'rgba(190,235,250,0.04)',
  line: 'rgba(190,235,250,0.4)',
  idle: '#2d4a5e',
  onAccent: '#06202b',
  surface: 'rgba(13,34,48,0.9)',
  surfaceSoft: 'rgba(13,34,48,0.6)',
  overlay: 'rgba(11,30,43,0.95)',
  sheet: 'rgba(10,30,44,0.5)',
  sheetShadow: 'rgba(0,0,0,0.6)',
  field: '#183447',
  fieldIdle: '#132c3d',
};

const DARK_GLASS = { ...GLASS };
const CHAMBER_GLASS: typeof GLASS = {
  top: 'rgba(22,58,80,0.86)',
  bottom: 'rgba(9,30,44,0.9)',
  hoverTop: 'rgba(30,72,98,0.9)',
  hoverBottom: 'rgba(12,38,55,0.92)',
  edge: 'rgba(160,230,245,0.35)',
  highlight: 'rgba(160,230,245,0.8)',
  highlightEdge: 'rgba(170,236,248,0.95)',
  highlightLine: 'rgba(200,245,252,1)',
  inner: 'rgba(190,235,250,0.04)',
};

export const PALETTE: Palette = { ...DARK_PALETTE };
let mode: UiMode = 'dark';

/**
 * Switches every canvas panel's colours. Called by each scene as it is built (the lobby
 * dark, a study room light); panels read PALETTE and GLASS when they draw, so anything
 * drawn after the switch follows it.
 */
export function setUiMode(next: UiMode): void {
  mode = next;
  Object.assign(PALETTE, next === 'chamber' ? CHAMBER_PALETTE : DARK_PALETTE);
  Object.assign(GLASS, next === 'chamber' ? CHAMBER_GLASS : DARK_GLASS);
}

export function uiMode(): UiMode {
  return mode;
}

/** A colour as text in the current mode (both modes are dark enough for data colours). */
export function inkFor(colour: string): string {
  return colour;
}

export const SERIES = [
  PALETTE.teal,
  PALETTE.navy,
  PALETTE.amber,
  PALETTE.violet,
  PALETTE.forest,
  PALETTE.crimson,
  PALETTE.grey,
];

export const FONT = BODY_FONT;

/** Global text scale with a floor: the Quest needs larger type than a desktop canvas. */
export const TEXT_SCALE = 1.3;
export const MIN_TEXT_PX = 10;

export function font(size: number, weight = 500, display = false): string {
  return `${weight} ${Math.max(MIN_TEXT_PX, Math.round(size * TEXT_SCALE))}px ${display ? DISPLAY_FONT : FONT}`;
}

/**
 * The background of a whole canvas panel: the glass surface edge to edge with rounded
 * corners (cut out by the panel's alpha test).
 */
export function panelBackground(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  radius = 16,
): void {
  ctx.clearRect(0, 0, w, h);
  if (mode === 'chamber') {
    // Thin glass with an illuminated edge and no bezel: a soft glow just inside the edge,
    // a frosted rim and the hairline of light; the glass itself is slightly see-through.
    const r = { x: 3, y: 3, w: w - 6, h: h - 6 };
    ctx.save();
    ctx.shadowColor = 'rgba(150,230,245,0.55)';
    ctx.shadowBlur = 8;
    glassPanel(ctx, r, radius);
    ctx.restore();
    ctx.lineWidth = 6;
    ctx.strokeStyle = 'rgba(200,240,250,0.07)';
    roundRect(ctx, r.x + 4, r.y + 4, r.w - 8, r.h - 8, Math.max(2, radius - 4));
    ctx.stroke();
    return;
  }
  glassPanel(ctx, { x: 0.5, y: 0.5, w: w - 1, h: h - 1 }, radius);
}

/**
 * A glass panel: vertical graphite gradient, faint edge, a hairline of cyan light along the
 * top and a brighter edge when highlighted. The design language's one surface.
 */
export function glassPanel(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  radius: number,
  opts: { highlight?: boolean; hovered?: boolean; edge?: string; hairline?: boolean } = {},
): void {
  const bg = ctx.createLinearGradient(0, r.y, 0, r.y + r.h);
  bg.addColorStop(0, opts.hovered ? GLASS.hoverTop : GLASS.top);
  bg.addColorStop(1, opts.hovered ? GLASS.hoverBottom : GLASS.bottom);
  ctx.fillStyle = bg;
  roundRect(ctx, r.x, r.y, r.w, r.h, radius);
  ctx.fill();
  ctx.lineWidth = opts.highlight ? 3 : 1.5;
  ctx.strokeStyle = opts.edge ?? (opts.highlight ? GLASS.highlightEdge : GLASS.edge);
  ctx.stroke();
  if (opts.hairline !== false) {
    const hl = ctx.createLinearGradient(r.x, 0, r.x + r.w, 0);
    hl.addColorStop(0, 'rgba(63,214,255,0)');
    hl.addColorStop(0.5, opts.highlight ? GLASS.highlightLine : GLASS.highlight);
    hl.addColorStop(1, 'rgba(63,214,255,0)');
    ctx.fillStyle = hl;
    ctx.fillRect(r.x + radius, r.y, r.w - radius * 2, 2);
  }
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

export function fillRoundRect(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  radius: number,
  fill: string | CanvasGradient,
): void {
  ctx.fillStyle = fill;
  roundRect(ctx, r.x, r.y, r.w, r.h, radius);
  ctx.fill();
}

export function text(
  ctx: CanvasRenderingContext2D,
  s: string,
  x: number,
  y: number,
  opts: {
    size?: number;
    weight?: number;
    colour?: string;
    align?: CanvasTextAlign;
    baseline?: CanvasTextBaseline;
    maxWidth?: number;
    /** Use the display face (titles, codes, big numbers). */
    display?: boolean;
  } = {},
): void {
  ctx.font = font(opts.size ?? 16, opts.weight ?? 500, opts.display);
  ctx.fillStyle = opts.colour ? inkFor(opts.colour) : PALETTE.text;
  ctx.textAlign = opts.align ?? 'left';
  ctx.textBaseline = opts.baseline ?? 'alphabetic';
  // Too long for its space: shorten with an ellipsis. Never squeeze the glyphs, which reads
  // as smudged text in the headset.
  ctx.fillText(opts.maxWidth ? truncate(ctx, s, opts.maxWidth) : s, x, y);
}

/** Greedy word wrap using the context's current font. */
export function wrapText(ctx: CanvasRenderingContext2D, s: string, maxWidth: number): string[] {
  const words = s.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const candidate = line ? `${line} ${w}` : w;
    if (ctx.measureText(candidate).width <= maxWidth || !line) line = candidate;
    else {
      lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines;
}

export function truncate(ctx: CanvasRenderingContext2D, s: string, maxWidth: number): string {
  if (ctx.measureText(s).width <= maxWidth) return s;
  let out = s;
  while (out.length > 1 && ctx.measureText(`${out}…`).width > maxWidth) out = out.slice(0, -1);
  return `${out}…`;
}

/* ---------- Scales ---------- */

export function linearScale(
  domainMin: number,
  domainMax: number,
  rangeMin: number,
  rangeMax: number,
): (v: number) => number {
  const d = domainMax - domainMin || 1;
  return (v: number) => rangeMin + ((v - domainMin) / d) * (rangeMax - rangeMin);
}

/** A "nice" axis maximum: 1, 2, 2.5, 5 or 10 times a power of ten, at or above the value. */
export function niceMax(value: number): number {
  if (value <= 0) return 1;
  const exp = Math.floor(Math.log10(value));
  const base = 10 ** exp;
  for (const m of [1, 2, 2.5, 5, 10]) if (m * base >= value) return m * base;
  return 10 * base;
}

export function ticks(max: number, count = 4): number[] {
  const step = max / count;
  return Array.from({ length: count + 1 }, (_, i) => Math.round(i * step * 100) / 100);
}

/* ---------- KPI tiles ---------- */

export interface Kpi {
  label: string;
  value: string;
  colour?: string;
  sub?: string;
}

export function kpiRow(
  ctx: CanvasRenderingContext2D,
  kpis: Kpi[],
  area: Rect,
  hoveredIndex = -1,
): Rect[] {
  const gap = 10;
  const w = (area.w - gap * (kpis.length - 1)) / kpis.length;
  return kpis.map((k, i) => {
    const r = { x: area.x + i * (w + gap), y: area.y, w, h: area.h };
    glassPanel(ctx, r, 10, { hovered: i === hoveredIndex, hairline: false });
    const label = k.label.toUpperCase();
    text(ctx, label, r.x + 12, r.y + 20, {
      size: fitSize(ctx, label, r.w - 20, 11, 8, 600),
      colour: PALETTE.muted,
      weight: 600,
      maxWidth: r.w - 20,
    });
    text(ctx, k.value, r.x + 12, r.y + 52, {
      size: 28,
      colour: k.colour ?? PALETTE.text,
      weight: 600,
      display: true,
      maxWidth: r.w - 20,
    });
    if (k.sub)
      text(ctx, k.sub, r.x + 12, r.y + 72, {
        size: fitSize(ctx, k.sub, r.w - 20, 11, 8),
        colour: PALETTE.muted,
        maxWidth: r.w - 20,
      });
    return r;
  });
}

/**
 * The largest logical size, from `size` down to `min`, at which `s` fits `width`. Text that
 * still does not fit at `min` is then shortened by `text`'s maxWidth.
 */
export function fitSize(
  ctx: CanvasRenderingContext2D,
  s: string,
  width: number,
  size: number,
  min: number,
  weight = 500,
): number {
  let n = size;
  for (; n > min; n--) {
    ctx.font = font(n, weight);
    if (ctx.measureText(s).width <= width) break;
  }
  return n;
}

/* ---------- Bars ---------- */

export interface BarDatum {
  label: string;
  values: number[];
  colours?: string[];
}

export interface BarOptions {
  stacked?: boolean;
  max?: number;
  colours?: string[];
  hoveredIndex?: number;
  showValues?: boolean;
  labelSize?: number;
  title?: string;
}

/** Vertical bars. Returns one rect per datum (the full column) for hit regions. */
export function bars(
  ctx: CanvasRenderingContext2D,
  data: BarDatum[],
  area: Rect,
  opts: BarOptions = {},
): Rect[] {
  const colours = opts.colours ?? SERIES;
  const titleH = opts.title ? 20 : 0;
  ctx.font = font(opts.labelSize ?? 10);
  const slot0 = (area.w - 40) / Math.max(1, data.length);
  const rotate = data.some((d) => ctx.measureText(d.label).width > slot0 - 4);
  const plot = {
    x: area.x + 34,
    y: area.y + titleH + 6,
    w: area.w - 40,
    h: area.h - titleH - (rotate ? 52 : 30),
  };
  if (opts.title)
    text(ctx, opts.title, area.x, area.y + 14, { size: 12, colour: PALETTE.muted, weight: 600 });
  const totals = data.map((d) =>
    opts.stacked ? d.values.reduce((a, b) => a + b, 0) : Math.max(...d.values, 0),
  );
  const max = opts.max ?? niceMax(Math.max(...totals, 1));
  const y = linearScale(0, max, plot.y + plot.h, plot.y);
  ctx.strokeStyle = PALETTE.grid;
  ctx.lineWidth = 1;
  for (const t of ticks(max)) {
    ctx.beginPath();
    ctx.moveTo(plot.x, y(t));
    ctx.lineTo(plot.x + plot.w, y(t));
    ctx.stroke();
    text(ctx, String(t), plot.x - 6, y(t) + 4, { size: 10, colour: PALETTE.muted, align: 'right' });
  }
  const n = data.length;
  const slot = plot.w / Math.max(1, n);
  const barW = Math.min(slot * 0.68, 60);
  const rects: Rect[] = [];
  data.forEach((d, i) => {
    const cx = plot.x + slot * i + slot / 2;
    const column = { x: cx - slot / 2, y: plot.y, w: slot, h: plot.h + 18 };
    if (i === opts.hoveredIndex) fillRoundRect(ctx, column, 6, PALETTE.hover);
    if (opts.stacked) {
      let acc = 0;
      d.values.forEach((v, s) => {
        const top = y(acc + v);
        const bottom = y(acc);
        ctx.fillStyle = d.colours?.[s] ?? colours[s % colours.length]!;
        ctx.fillRect(cx - barW / 2, top, barW, Math.max(0, bottom - top));
        acc += v;
      });
    } else {
      const gw = barW / d.values.length;
      d.values.forEach((v, s) => {
        ctx.fillStyle = d.colours?.[s] ?? colours[s % colours.length]!;
        ctx.fillRect(cx - barW / 2 + gw * s, y(v), gw - 1, plot.y + plot.h - y(v));
      });
    }
    if (opts.showValues)
      text(ctx, String(totals[i]), cx, y(totals[i]!) - 4, {
        size: 10,
        colour: PALETTE.text,
        align: 'center',
      });
    ctx.font = font(opts.labelSize ?? 10);
    if (rotate) {
      ctx.save();
      ctx.translate(cx + 4, plot.y + plot.h + 8);
      ctx.rotate(-Math.PI / 5);
      text(ctx, truncate(ctx, d.label, 60), 0, 0, {
        size: opts.labelSize ?? 10,
        colour: PALETTE.muted,
        align: 'right',
      });
      ctx.restore();
    } else {
      text(ctx, truncate(ctx, d.label, slot - 4), cx, plot.y + plot.h + 14, {
        size: opts.labelSize ?? 10,
        colour: PALETTE.muted,
        align: 'center',
      });
    }
    rects.push(column);
  });
  return rects;
}

/** Horizontal bars with labels on the left. Returns one rect per row. */
export function hbars(
  ctx: CanvasRenderingContext2D,
  data: { label: string; value: number; colour?: string; sub?: string }[],
  area: Rect,
  opts: { max?: number; hoveredIndex?: number; title?: string; labelWidth?: number } = {},
): Rect[] {
  const titleH = opts.title ? 20 : 0;
  if (opts.title)
    text(ctx, opts.title, area.x, area.y + 14, { size: 12, colour: PALETTE.muted, weight: 600 });
  const labelW = opts.labelWidth ?? 110;
  const rowH = Math.min(30, (area.h - titleH) / Math.max(1, data.length));
  const max = opts.max ?? niceMax(Math.max(...data.map((d) => d.value), 1));
  // Leave room on the right for the longest value text, up to 40% of the width.
  ctx.font = font(11);
  const subW = Math.min(
    area.w * 0.4,
    Math.max(36, ...data.map((d) => ctx.measureText(d.sub ?? String(d.value)).width)) + 10,
  );
  const x = linearScale(0, max, area.x + labelW, area.x + area.w - subW);
  return data.map((d, i) => {
    const r = { x: area.x, y: area.y + titleH + i * rowH, w: area.w, h: rowH };
    if (i === opts.hoveredIndex) fillRoundRect(ctx, r, 6, PALETTE.hover);
    ctx.font = font(11);
    text(ctx, truncate(ctx, d.label, labelW - 10), r.x + 4, r.y + rowH / 2 + 4, {
      size: 11,
      colour: PALETTE.text,
    });
    ctx.fillStyle = PALETTE.grid;
    ctx.fillRect(x(0), r.y + rowH * 0.3, x(max) - x(0), rowH * 0.4);
    ctx.fillStyle = d.colour ?? PALETTE.teal;
    ctx.fillRect(x(0), r.y + rowH * 0.3, Math.max(0, x(d.value) - x(0)), rowH * 0.4);
    text(ctx, d.sub ?? String(d.value), x(max) + 6, r.y + rowH / 2 + 4, {
      size: 11,
      colour: PALETTE.muted,
      maxWidth: area.x + area.w - x(max) - 8,
    });
    return r;
  });
}

/* ---------- Lines ---------- */

export interface LineSeries {
  values: number[];
  colour: string;
  dashed?: boolean;
  fill?: boolean;
  label?: string;
}

export function lines(
  ctx: CanvasRenderingContext2D,
  series: LineSeries[],
  labels: string[],
  area: Rect,
  opts: { max?: number; title?: string; hoveredIndex?: number } = {},
): Rect[] {
  // Legend: beside the title when both fit, otherwise on its own row under the title, so it
  // never sits on top of the data.
  ctx.font = font(10);
  const legendW = series
    .filter((s) => s.label)
    .reduce((w, s) => w + 14 + ctx.measureText(s.label!).width + 16, 0);
  ctx.font = font(12, 600);
  const titleW = opts.title ? ctx.measureText(opts.title).width : 0;
  const onTitleRow = legendW === 0 || titleW + legendW + 16 <= area.w;
  const titleH = (opts.title ? 20 : 0) + (onTitleRow ? 0 : 18);
  if (opts.title)
    text(ctx, opts.title, area.x, area.y + 14, { size: 12, colour: PALETTE.muted, weight: 600 });
  const plot = { x: area.x + 34, y: area.y + titleH + 6, w: area.w - 40, h: area.h - titleH - 28 };
  const max = opts.max ?? niceMax(Math.max(1, ...series.flatMap((s) => s.values)));
  const y = linearScale(0, max, plot.y + plot.h, plot.y);
  const n = Math.max(...series.map((s) => s.values.length), 2);
  const x = linearScale(0, n - 1, plot.x, plot.x + plot.w);
  ctx.strokeStyle = PALETTE.grid;
  for (const t of ticks(max)) {
    ctx.beginPath();
    ctx.moveTo(plot.x, y(t));
    ctx.lineTo(plot.x + plot.w, y(t));
    ctx.stroke();
    text(ctx, String(t), plot.x - 6, y(t) + 4, { size: 10, colour: PALETTE.muted, align: 'right' });
  }
  for (const s of series) {
    ctx.beginPath();
    s.values.forEach((v, i) => (i === 0 ? ctx.moveTo(x(i), y(v)) : ctx.lineTo(x(i), y(v))));
    if (s.fill) {
      ctx.lineTo(x(s.values.length - 1), y(0));
      ctx.lineTo(x(0), y(0));
      ctx.closePath();
      ctx.fillStyle = s.colour
        .replace(')', ', 0.18)')
        .replace('rgb(', 'rgba(')
        .replace('#', 'rgba(');
      ctx.fillStyle = hexToRgba(s.colour, 0.18);
      ctx.fill();
      ctx.beginPath();
      s.values.forEach((v, i) => (i === 0 ? ctx.moveTo(x(i), y(v)) : ctx.lineTo(x(i), y(v))));
    }
    ctx.strokeStyle = s.colour;
    ctx.lineWidth = 2;
    ctx.setLineDash(s.dashed ? [6, 4] : []);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  // Sparse x labels: the last one always shows; any label that would touch it is dropped.
  const every = Math.max(1, Math.ceil(labels.length / 6));
  ctx.font = font(9);
  const lastIndex = labels.length - 1;
  const lastW = lastIndex >= 0 ? ctx.measureText(labels[lastIndex]!).width : 0;
  labels.forEach((l, i) => {
    if (i % every !== 0 && i !== lastIndex) return;
    if (i !== lastIndex) {
      const w = ctx.measureText(l).width;
      if (x(i) + w / 2 + 8 > x(lastIndex) - lastW / 2) return;
    }
    text(ctx, l, x(i), plot.y + plot.h + 14, { size: 9, colour: PALETTE.muted, align: 'center' });
    ctx.font = font(9);
  });
  if (opts.hoveredIndex !== undefined && opts.hoveredIndex >= 0) {
    ctx.strokeStyle = PALETTE.line;
    ctx.beginPath();
    ctx.moveTo(x(opts.hoveredIndex), plot.y);
    ctx.lineTo(x(opts.hoveredIndex), plot.y + plot.h);
    ctx.stroke();
  }
  // Legend (measured above).
  let lx = onTitleRow ? area.x + area.w - legendW : area.x;
  const ly = onTitleRow ? area.y + 14 : area.y + 34;
  ctx.font = font(10);
  for (const s of series) {
    if (!s.label) continue;
    ctx.fillStyle = s.colour;
    ctx.fillRect(lx, ly - 5, 10, 3);
    text(ctx, s.label, lx + 14, ly, { size: 10, colour: PALETTE.muted });
    lx += 14 + ctx.measureText(s.label).width + 16;
  }
  // Hit columns.
  const colW = plot.w / Math.max(1, n - 1);
  return Array.from({ length: n }, (_, i) => ({
    x: x(i) - colW / 2,
    y: plot.y,
    w: colW,
    h: plot.h,
  }));
}

export function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return hex;
  return `rgba(${parseInt(m[1]!, 16)}, ${parseInt(m[2]!, 16)}, ${parseInt(m[3]!, 16)}, ${alpha})`;
}

/* ---------- Heatmap ---------- */

export function heatmap(
  ctx: CanvasRenderingContext2D,
  rows: string[],
  cols: string[],
  value: (r: number, c: number) => number | undefined,
  area: Rect,
  opts: {
    max?: number;
    hovered?: [number, number];
    labelWidth?: number;
    colour?: (t: number) => string;
    format?: (v: number) => string;
  } = {},
): Rect[][] {
  const labelW = opts.labelWidth ?? 120;
  const headH = 20;
  const cellW = (area.w - labelW) / Math.max(1, cols.length);
  const cellH = (area.h - headH) / Math.max(1, rows.length);
  const max = opts.max ?? 1;
  const colour = opts.colour ?? ((t) => `rgba(212, 58, 82, ${0.12 + 0.75 * t})`);
  // Column headers: level and centred when every header fits its cell, otherwise slanted.
  ctx.font = font(9, 600);
  const level = cols.every((c) => ctx.measureText(c).width <= cellW - 4);
  cols.forEach((c, ci) => {
    const cx = area.x + labelW + ci * cellW + cellW / 2;
    if (level) {
      text(ctx, c, cx, area.y + headH - 6, {
        size: 9,
        weight: 600,
        colour: PALETTE.muted,
        align: 'center',
      });
      return;
    }
    ctx.save();
    ctx.translate(cx - cellW / 4, area.y + headH - 4);
    ctx.rotate(-0.5);
    text(ctx, c, 0, 0, {
      size: 9,
      weight: 600,
      colour: PALETTE.muted,
      align: 'left',
      maxWidth: 70,
    });
    ctx.restore();
  });
  return rows.map((r, ri) => {
    ctx.font = font(10);
    text(ctx, truncate(ctx, r, labelW - 8), area.x, area.y + headH + ri * cellH + cellH / 2 + 4, {
      size: 10,
      colour: PALETTE.text,
    });
    return cols.map((_, ci) => {
      const rect = {
        x: area.x + labelW + ci * cellW,
        y: area.y + headH + ri * cellH,
        w: cellW,
        h: cellH,
      };
      const v = value(ri, ci);
      if (v === undefined) {
        ctx.fillStyle = PALETTE.faint;
        ctx.fillRect(rect.x + 1, rect.y + 1, rect.w - 2, rect.h - 2);
        return rect;
      }
      ctx.fillStyle = colour(Math.min(1, v / max));
      ctx.fillRect(rect.x + 1, rect.y + 1, rect.w - 2, rect.h - 2);
      if (opts.hovered && opts.hovered[0] === ri && opts.hovered[1] === ci) {
        ctx.strokeStyle = PALETTE.text;
        ctx.lineWidth = 2;
        ctx.strokeRect(rect.x + 1, rect.y + 1, rect.w - 2, rect.h - 2);
      }
      if (cellW > 34 && cellH > 14)
        text(
          ctx,
          opts.format ? opts.format(v) : String(v),
          rect.x + rect.w / 2,
          rect.y + rect.h / 2 + 3,
          { size: 9, colour: PALETTE.text, align: 'center' },
        );
      return rect;
    });
  });
}

/* ---------- Treemap (squarified) ---------- */

export interface TreemapItem {
  label: string;
  value: number;
  colour?: string;
}

export function squarify(items: TreemapItem[], area: Rect): (Rect & { item: TreemapItem })[] {
  const total = items.reduce((s, i) => s + i.value, 0);
  const sorted = items.filter((i) => i.value > 0).sort((a, b) => b.value - a.value);
  const out: (Rect & { item: TreemapItem })[] = [];
  if (total <= 0 || sorted.length === 0) return out;
  let rect = { ...area };
  const scale = (area.w * area.h) / total;
  let row: TreemapItem[] = [];
  const worst = (r: TreemapItem[], w: number) => {
    const s = r.reduce((a, i) => a + i.value * scale, 0);
    const mx = Math.max(...r.map((i) => i.value * scale));
    const mn = Math.min(...r.map((i) => i.value * scale));
    return Math.max((w * w * mx) / (s * s), (s * s) / (w * w * mn));
  };
  const layoutRow = (r: TreemapItem[]) => {
    const s = r.reduce((a, i) => a + i.value * scale, 0);
    const horizontal = rect.w >= rect.h;
    const length = horizontal ? rect.h : rect.w;
    const thickness = s / length;
    let offset = 0;
    for (const item of r) {
      const len = (item.value * scale) / thickness;
      out.push(
        horizontal
          ? { x: rect.x, y: rect.y + offset, w: thickness, h: len, item }
          : { x: rect.x + offset, y: rect.y, w: len, h: thickness, item },
      );
      offset += len;
    }
    if (horizontal) {
      rect = { x: rect.x + thickness, y: rect.y, w: rect.w - thickness, h: rect.h };
    } else {
      rect = { x: rect.x, y: rect.y + thickness, w: rect.w, h: rect.h - thickness };
    }
  };
  for (const item of sorted) {
    const w = Math.min(rect.w, rect.h);
    if (row.length === 0 || worst([...row, item], w) <= worst(row, w)) row.push(item);
    else {
      layoutRow(row);
      row = [item];
    }
  }
  if (row.length) layoutRow(row);
  return out;
}

export function treemap(
  ctx: CanvasRenderingContext2D,
  items: TreemapItem[],
  area: Rect,
  opts: { hoveredIndex?: number; title?: string } = {},
): (Rect & { item: TreemapItem })[] {
  const titleH = opts.title ? 20 : 0;
  if (opts.title)
    text(ctx, opts.title, area.x, area.y + 14, { size: 12, colour: PALETTE.muted, weight: 600 });
  const cells = squarify(items, { x: area.x, y: area.y + titleH, w: area.w, h: area.h - titleH });
  cells.forEach((c, i) => {
    ctx.fillStyle = c.item.colour ?? SERIES[i % SERIES.length]!;
    ctx.fillRect(c.x + 1, c.y + 1, c.w - 2, c.h - 2);
    if (i === opts.hoveredIndex) {
      ctx.fillStyle = PALETTE.hover;
      ctx.fillRect(c.x + 1, c.y + 1, c.w - 2, c.h - 2);
    }
    if (c.w > 50 && c.h > 24) {
      ctx.font = font(11, 600);
      text(ctx, truncate(ctx, c.item.label, c.w - 10), c.x + 6, c.y + 16, {
        size: 11,
        weight: 600,
      });
      text(ctx, String(c.item.value), c.x + 6, c.y + 32, {
        size: 12,
        colour: 'rgba(255,255,255,0.8)',
      });
    }
  });
  return cells;
}

/* ---------- Funnel ---------- */

export function funnel(
  ctx: CanvasRenderingContext2D,
  stages: { label: string; value: number }[],
  area: Rect,
  opts: { title?: string; hoveredIndex?: number; colours?: string[] } = {},
): Rect[] {
  const titleH = opts.title ? 20 : 0;
  if (opts.title)
    text(ctx, opts.title, area.x, area.y + 14, { size: 12, colour: PALETTE.muted, weight: 600 });
  const max = Math.max(1, ...stages.map((s) => s.value));
  const rowH = (area.h - titleH) / Math.max(1, stages.length);
  const colours = opts.colours ?? [
    PALETTE.grey,
    PALETTE.navy,
    PALETTE.teal,
    PALETTE.forest,
    PALETTE.violet,
  ];
  return stages.map((s, i) => {
    const w = (s.value / max) * (area.w - 90);
    const r = { x: area.x, y: area.y + titleH + i * rowH, w: area.w, h: rowH };
    if (i === opts.hoveredIndex) fillRoundRect(ctx, r, 6, PALETTE.hover);
    ctx.fillStyle = colours[i % colours.length]!;
    roundRect(
      ctx,
      area.x + 90 + (area.w - 90 - w) / 2,
      r.y + rowH * 0.15,
      Math.max(2, w),
      rowH * 0.7,
      4,
    );
    ctx.fill();
    text(ctx, s.label, area.x + 84, r.y + rowH / 2 + 4, {
      size: 11,
      align: 'right',
      colour: PALETTE.text,
    });
    text(ctx, String(s.value), area.x + 90 + area.w / 2 - 45, r.y + rowH / 2 + 4, {
      size: 11,
      align: 'center',
      weight: 600,
    });
    return r;
  });
}

/* ---------- Radar ---------- */

export function radar(
  ctx: CanvasRenderingContext2D,
  axes: string[],
  values: number[],
  area: Rect,
  opts: { colour?: string; title?: string } = {},
): void {
  const titleH = opts.title ? 20 : 0;
  if (opts.title)
    text(ctx, opts.title, area.x, area.y + 14, { size: 12, colour: PALETTE.muted, weight: 600 });
  const cx = area.x + area.w / 2;
  const cy = area.y + titleH + (area.h - titleH) / 2;
  const radius = Math.min(area.w, area.h - titleH) / 2 - 26;
  const n = axes.length;
  const angle = (i: number) => -Math.PI / 2 + (i / n) * Math.PI * 2;
  ctx.strokeStyle = PALETTE.grid;
  for (const t of [0.25, 0.5, 0.75, 1]) {
    ctx.beginPath();
    for (let i = 0; i <= n; i++) {
      const a = angle(i % n);
      const px = cx + Math.cos(a) * radius * t;
      const py = cy + Math.sin(a) * radius * t;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
  }
  axes.forEach((label, i) => {
    const a = angle(i);
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(a) * radius, cy + Math.sin(a) * radius);
    ctx.stroke();
    text(ctx, label, cx + Math.cos(a) * (radius + 14), cy + Math.sin(a) * (radius + 14) + 4, {
      size: 9,
      colour: PALETTE.muted,
      align: 'center',
    });
  });
  ctx.beginPath();
  values.forEach((v, i) => {
    const a = angle(i);
    const r = radius * Math.min(1, Math.max(0, v));
    if (i === 0) ctx.moveTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    else ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  });
  ctx.closePath();
  ctx.fillStyle = hexToRgba(opts.colour ?? PALETTE.teal, 0.3);
  ctx.fill();
  ctx.strokeStyle = opts.colour ?? PALETTE.teal;
  ctx.lineWidth = 2;
  ctx.stroke();
}

/* ---------- Lists ---------- */

export interface ListRow {
  cells: string[];
  colour?: string;
  flag?: string;
}

export function list(
  ctx: CanvasRenderingContext2D,
  header: string[],
  rows: ListRow[],
  area: Rect,
  opts: {
    hoveredIndex?: number;
    widths?: number[];
    rowH?: number;
    title?: string;
    emptyText?: string;
  } = {},
): Rect[] {
  const titleH = opts.title ? 20 : 0;
  if (opts.title)
    text(ctx, opts.title, area.x, area.y + 14, { size: 12, colour: PALETTE.muted, weight: 600 });
  const rowH = opts.rowH ?? 22;
  const widths = opts.widths ?? header.map(() => 1 / header.length);
  const xs: number[] = [];
  let acc = area.x;
  for (const w of widths) {
    xs.push(acc);
    acc += w * area.w;
  }
  header.forEach((h, i) =>
    text(ctx, h.toUpperCase(), xs[i]! + 4, area.y + titleH + 12, {
      size: fitSize(ctx, h.toUpperCase(), widths[i]! * area.w - 8, 9, 7, 600),
      colour: PALETTE.muted,
      weight: 600,
      maxWidth: widths[i]! * area.w - 8,
    }),
  );
  ctx.strokeStyle = PALETTE.grid;
  ctx.beginPath();
  ctx.moveTo(area.x, area.y + titleH + 17);
  ctx.lineTo(area.x + area.w, area.y + titleH + 17);
  ctx.stroke();
  const maxRows = Math.floor((area.h - titleH - 20) / rowH);
  if (rows.length === 0 && opts.emptyText)
    text(ctx, opts.emptyText, area.x + 4, area.y + titleH + 40, {
      size: 11,
      colour: PALETTE.muted,
    });
  return rows.slice(0, maxRows).map((r, i) => {
    const rect = { x: area.x, y: area.y + titleH + 20 + i * rowH, w: area.w, h: rowH };
    if (i === opts.hoveredIndex) fillRoundRect(ctx, rect, 4, PALETTE.hover);
    if (r.flag) {
      ctx.fillStyle = r.colour ?? PALETTE.amber;
      ctx.fillRect(rect.x, rect.y + 4, 3, rowH - 8);
    }
    r.cells.forEach((c, ci) => {
      ctx.font = font(11);
      const w = widths[ci]! * area.w - 8;
      text(ctx, truncate(ctx, c, w), xs[ci]! + 6, rect.y + rowH / 2 + 4, {
        size: 11,
        colour: ci === 0 ? PALETTE.text : PALETTE.muted,
      });
    });
    return rect;
  });
}

/* ---------- Buttons and chips ---------- */

export function chip(
  ctx: CanvasRenderingContext2D,
  label: string,
  x: number,
  y: number,
  opts: {
    active?: boolean;
    hovered?: boolean;
    colour?: string;
    h?: number;
    minW?: number;
    size?: number;
  } = {},
): Rect {
  const size = opts.size ?? 11;
  ctx.font = font(size, 600);
  const w = Math.max(opts.minW ?? 0, ctx.measureText(label).width + 20);
  const h = opts.h ?? 24;
  const r = { x, y, w, h };
  fillRoundRect(
    ctx,
    r,
    h / 2,
    opts.active
      ? (opts.colour ?? PALETTE.teal)
      : opts.hovered
        ? PALETTE.raisedHover
        : PALETTE.raised,
  );
  text(ctx, label, x + w / 2, y + h / 2 + size * 0.36, {
    size,
    weight: 600,
    align: 'center',
    colour: opts.active ? PALETTE.onAccent : PALETTE.text,
  });
  return r;
}

export function sparkline(
  ctx: CanvasRenderingContext2D,
  values: number[],
  area: Rect,
  colour = PALETTE.teal,
  bands?: { low: number; high: number },
): void {
  const max = Math.max(1, ...values, bands?.high ?? 0);
  const min = Math.min(0, ...values, bands?.low ?? 0);
  const x = linearScale(0, Math.max(1, values.length - 1), area.x, area.x + area.w);
  const y = linearScale(min, max, area.y + area.h, area.y);
  if (bands) {
    ctx.fillStyle = 'rgba(76,175,106,0.15)';
    ctx.fillRect(area.x, y(bands.high), area.w, y(bands.low) - y(bands.high));
  }
  ctx.beginPath();
  values.forEach((v, i) => (i === 0 ? ctx.moveTo(x(i), y(v)) : ctx.lineTo(x(i), y(v))));
  ctx.strokeStyle = colour;
  ctx.lineWidth = 1.5;
  ctx.stroke();
  values.forEach((v, i) => {
    ctx.fillStyle = bands && (v > bands.high || v < bands.low) ? PALETTE.crimson : colour;
    ctx.beginPath();
    ctx.arc(x(i), y(v), 2, 0, Math.PI * 2);
    ctx.fill();
  });
}
